import type {ConfigInfoParameters, InterfaceDescriptor, VariantInfo} from "#template/interface-descriptors"
import {ConfigInfo, wrapInterfaceId} from "#template/interface-descriptors"
import {getTemplateNodeForObjectPath} from "#template/object-path-utils"
import {valueChanged} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {ObjectPathPart} from "#template/interactions"
import type {TemplateNode} from "#template/template-node/template-node"

const TD = TypeDescriptors

export type OutputAffectedParts = Map<string, {parts: ObjectPathPart[]; reExportIds: string[]}>

export type TaggedInputSourceMapping = {
    mapping: Map<string, TemplateNode[]>
    rawDescriptors: InterfaceDescriptor<unknown>[]
    nodeId: string
}

export const inputSourceMappingDesc = TD.Map<string, TemplateNode[]>(TD.Array(TD.Identity<TemplateNode>()))

const entryPartsDesc = TD.Array(TD.ObjectPathPart)
const entryReExportIdsDesc = TD.Array(TD.String)
const outputAffectedEntryDesc: TypeDescriptors.Desc<{parts: ObjectPathPart[]; reExportIds: string[]}> = {
    deepCompare: (a, b) => {
        if (!a || !b) return a === b
        return !valueChanged(a.parts, b.parts, entryPartsDesc) && !valueChanged(a.reExportIds, b.reExportIds, entryReExportIdsDesc)
    },
}
export const outputAffectedObjectPathPartsDesc = TD.Map<string, {parts: ObjectPathPart[]; reExportIds: string[]}>(outputAffectedEntryDesc)

const isSortable = (x: InterfaceDescriptor<unknown, {}>): x is InterfaceDescriptor<VariantInfo | null, ConfigInfoParameters & {order: number}> => {
    return x instanceof ConfigInfo && x.props.order !== undefined
}

// For each input descriptor it folds in:
//  - input chaining: affected objects of claimed sub-template inputs (matched by source-node identity),
//  - output chaining: where a consumed Output places the value it pulls (matched by export id),
//  - multi-level: a consumed output's reExportIds become this descriptor's affectedOutputIds for the next level.
// Output ids are instance-qualified — exposed descriptors are pre-wrapped by FilterAndWrapInterface, claimed ones
// (read from the unwrapped rawDescriptors) are wrapped here with the claimed instance's nodeId.
export function chainDescriptors(
    descriptorList: InterfaceDescriptor<unknown>[],
    taggedMappings: TaggedInputSourceMapping[],
    outputAffectedObjectPathParts: OutputAffectedParts,
): InterfaceDescriptor<unknown>[] {
    const merged = descriptorList.map((desc) => {
        if (desc.props.type !== "input" || !desc.props.affectedObjectPathParts) return desc

        const affectedNodes = new Set(desc.props.affectedObjectPathParts.map((p) => getTemplateNodeForObjectPath(p.path)))

        const extraPathParts: ObjectPathPart[] = []
        const claimedOutputIds: string[] = []
        for (const {mapping, rawDescriptors, nodeId} of taggedMappings) {
            for (const [inputId, sourceNodes] of mapping) {
                if (!sourceNodes.some((n) => affectedNodes.has(n))) continue

                const claimedDesc = rawDescriptors.find((d) => d.props.id === inputId)
                if (claimedDesc?.props.affectedObjectPathParts) {
                    extraPathParts.push(...claimedDesc.props.affectedObjectPathParts)
                }
                if (claimedDesc?.props.affectedOutputIds) {
                    claimedOutputIds.push(...claimedDesc.props.affectedOutputIds.map((outputId) => wrapInterfaceId(nodeId, outputId)))
                }
            }
        }

        const reExportIds: string[] = []
        let consumedOutput = false
        for (const outputId of [...(desc.props.affectedOutputIds ?? []), ...claimedOutputIds]) {
            const output = outputAffectedObjectPathParts.get(outputId)
            if (!output) continue
            consumedOutput = true
            extraPathParts.push(...output.parts)
            reExportIds.push(...output.reExportIds)
        }

        if (extraPathParts.length === 0 && !consumedOutput) return desc

        const cloned = desc.clone(desc.props.id, desc.props.isSetByTemplate)
        cloned.props.affectedObjectPathParts = [...desc.props.affectedObjectPathParts, ...extraPathParts]
        if (consumedOutput) cloned.props.affectedOutputIds = reExportIds
        return cloned
    })

    const sortedDescriptors = [...merged.filter(isSortable)].sort((a, b) => a.props.order - b.props.order)
    const nonSortableDescriptors = merged.filter((x) => !isSortable(x))
    return [...sortedDescriptors, ...nonSortableDescriptors]
}
